<template>
        <!-- Reuse Transfer Button -->
        <button-tertiary
        class="reuse-transfer-button"
        icon="cycle"
        :label="t('transfers.instantBAI.step1.reuseTransfer')"
        @clicked="emit('openReuseTransferModal')"/>
        <!-- Origin Account Dropdown Section -->
        <!-- Divider -->
        <divider-generic
        :applyBorder="false"
        :dividerSize="'medium'"
        :icon="'account-balance'"
        :title="t('transfers.instantBAI.step1.dividers.originAccount')"/>
        <!-- Account Dropdown -->
        <dropdown-accounts
        :params="originAccountParams"
        :options="getAccountsBalances"
        :selectedOption="formData.originAccount"
        @clicked="originAccountDropdownClickHandler"/>
        <!-- Operation Data Section -->
        <!-- Divider -->
        <divider-generic
        :dividerSize="'medium'"
        :icon="'tag'"
        :title="t('transfers.instantBAI.step1.dividers.operationData')"/>
        <!-- Operations Types Inputs -->
        <operation-type-section
        :formData="formData"
        :showInputsErrors="showInputsErrors"
        :fieldsWithError="fieldsWithError"
        :clearForm="clearForm"
        @updateFormData="emit('updateFormData', $event)"
        @requestOperationsTypes="emit('requestOperationsTypes')"
        @requestCategories="emit('requestCategories')"
        @resetShowInputsErrors="emit('resetShowInputsErrors')"
        @formIsCleared="emit('formIsCleared')"
        @scrollTo="emit('scrollTo', $event)"/>
        <!-- Amount Section -->
        <!-- Divider -->
        <divider-generic
        :dividerSize="'medium'"
        :icon="'bills'"
        :title="t('transfers.instantBAI.step1.dividers.amount')"/>
        <!-- Amount Inputs -->
        <transfer-amount-section
        :formData="formData"
        :showInputsErrors="showInputsErrors"
        :fieldsWithError="fieldsWithError"
        :estimatedAmountError="estimatedAmountError"
        :clearForm="clearForm"
        @updateFormData="emit('updateFormData', $event)"
        @calculateEstimatedAmount="emit('calculateEstimatedAmount')"
        @formIsCleared="emit('formIsCleared')"
        @scrollTo="emit('scrollTo', $event)"/>
        <!-- Destination Section -->
        <!-- Divider -->
        <divider-generic
        :dividerSize="'medium'"
        :icon="'group'"
        :title="t('transfers.instantBAI.step1.dividers.destination')"/>
        <!-- Destination Inputs -->
        <transfer-destination-section
        :formData="formData"
        :showInputsErrors="showInputsErrors"
        :fieldsWithError="fieldsWithError"
        :invalidIbanErrorMessage="invalidIbanErrorMessage"
        :invalidIbanError="invalidIbanError"
        :clearForm="clearForm"
        :validatedIban="validatedIban"
        :unvalidatedSwift="unvalidatedSwift"
        :unvalidatedSwiftErrorMessage="unvalidatedSwiftErrorMessage"
        :applyOwnCustomerInfo="applyOwnCustomerInfo"
        @updateFormData="emit('updateFormData', $event)"
        @validateIban="emit('validateIban')"
        @resetShowInputsErrors="emit('resetShowInputsErrors')"
        @formIsCleared="emit('formIsCleared')"
        @scrollTo="emit('scrollTo', $event)"/>
</template>

<script setup>
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DividerGeneric from '@/components/generic/dividers/Generic'
import DropdownAccounts from '@/components/generic/dropdowns/accounts/Account'
import OperationTypeSection from '@/components/features/transfers/instantBAI/OperationTypeSection'
import TransferAmountSection from '@/components/features/transfers/instantBAI/TransferAmountSection'
import TransferDestinationSection from '@/components/features/transfers/instantBAI/TransferDestinationSection'

/* Vue */
import { ref } from 'vue'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getAccountsBalances } = mapGetters(moduleMapper.TRANSFERS_INSTANT_BAI)

/** Props
@formData {Object} - Object with form data
@validatedIban {Boolean} - Boolean controller show validated iban inputs
@showInputsErrors {Boolean} - Prop to trigger display of inputs errors
@clearForm {Boolean} - Boolean controller to clear form and errors
@applyOwnCustomerInfo {Boolean} Boolean controller to show Own Customer Info
@estimatedAmountError {Boolean} Boolean controller to apply specific amount error
@fieldsWithError {Array} - Input List with error MDW provides to use
@invalidIbanError {Boolean} Boolean controller to apply iban error
@invalidIbanErrorMessage {String} Mdw Iban Error Message
*/
const props = defineProps({
    formData: Object,
    ibanDetails: Object,
    validatedIban: Boolean,
	showInputsErrors: Boolean,
    applyOwnCustomerInfo: Boolean,
    clearForm: Boolean,
    estimatedAmountError: Boolean,
    fieldsWithError: Array,
    invalidIbanError: Boolean,
    invalidIbanErrorMessage: String
})

const emit = defineEmits([
    'openReuseTransferModal',
    'updateFormData',
    'calculateEstimatedAmount',
    'validateIban',
    'requestOperationsTypes',
    'requestCategories',
    'resetShowInputsErrors',
    'scrollTo',
    'formIsCleared'
])

/*************************************************
*                                                *
*              Origin Account Data               *
*                                                *
*************************************************/

const originAccountParams = ref({
    labelText: t('transfers.instantBAI.genericLabels.account')
})

const originAccountDropdownClickHandler = event => {
    const accountDisplayInfo = getAccountsBalances.value.find(account => account.id === event).accountNumber

    emit('updateFormData', { field: 'accountDisplayInfo', inputValue: accountDisplayInfo })
    emit('updateFormData', { field: 'originAccount', inputValue: event })
}
</script>

<style lang="scss" scoped>
.reuse-transfer-button {
    align-self: center;
    margin-bottom: 24px;
}

.tabs-segment-wrapper {
    align-self: center;
    :deep(.tab) {
        min-width: 200px;
    }
}
</style>
